import { useCallback } from "react";
import * as Sentry from "@sentry/react";
import { useStateValue } from "../context/StateValueContext";
import { useApi } from "./useApi";
import { hasPermission } from "../functions/permissionUtils";
import { PERMISSIONS } from "../constants/permissions";
import { CompanySettings } from "../types/companySettings";
import { User } from "../types/user";

type ClientSettings = Pick<
  CompanySettings,
  "disputes_enabled" | "order_insights_enabled" | "vamp_rates_enabled"
>;

// Loads the caller's own entitlement flags. Called on sign-in, after a company
// switch, and from the retry on surfaces that a failed load left unresolvable.
export function useFetchCompanySettings() {
  const { get } = useApi();
  const [, dispatch] = useStateValue();

  return useCallback(
    async (user: User | null) => {
      // Admins/agents (all-company access) are never gated, so skip the fetch.
      if (
        hasPermission(user, PERMISSIONS.DISPUTES_ALL_COMPANIES) ||
        hasPermission(user, PERMISSIONS.OI_ALL)
      ) {
        return;
      }

      dispatch({ type: "SET_COMPANY_SETTINGS", settings: null });

      try {
        const settings = await get<ClientSettings>(`/api/client-settings`);
        dispatch({
          type: "SET_COMPANY_SETTINGS",
          settings: {
            disputesEnabled: settings.disputes_enabled,
            orderInsightsEnabled: settings.order_insights_enabled,
            vampRatesEnabled: settings.vamp_rates_enabled,
          },
        });
      } catch (err) {
        dispatch({ type: "SET_COMPANY_SETTINGS_FAILED" });
        Sentry.captureException(err, {
          tags: { component: "company-settings", action: "fetch" },
        });
      }
    },
    [get, dispatch],
  );
}
